import React, { useEffect, useState } from 'react';
import { Typography, Spin, Alert } from 'antd';
import { LoadingOutlined, RobotOutlined } from '@ant-design/icons';
import {
  fetchSingleFormById,
  Form as FormType,
} from '../../../../../../api/formApi';
import UnifiedFormRenderer, { flattenFields } from '../../../../../../components/forms/FormRenderer';
import { EvidenceRef } from '../../../../../../types/healthcare/PatientAnalysisTypes';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  assignedFormId?: number | null;
  aiSuggestions?: Record<string, any>;
  /** Per-field evidence references for the AI-filled values, keyed by field id. */
  aiSuggestionRefs?: Record<string, EvidenceRef[]>;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
  readOnly?: boolean;
  initialResponses?: Record<string, any>;
}

/**
 * Renders a treatment-plan "complete practitioner form" task inline. Thin
 * wrapper around the shared `UnifiedFormRenderer` (compact, flat) — keeps the
 * AI-suggestion prefill and the `onChange(payload, isValid)` contract used by
 * the plan drawer; the renderer owns field rendering + required validation.
 */
const CompletePractitionerFormTaskForm: React.FC<Props> = ({ taskConfig, assignedFormId, aiSuggestions, aiSuggestionRefs, onChange, readOnly, initialResponses }) => {
  const formId = taskConfig.form_id;
  const [responses, setResponses] = useState<Record<string | number, any>>(initialResponses ?? {});
  const [files, setFiles] = useState<Record<string | number, File[]>>({});
  const [form, setForm] = useState<FormType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (!formId) return;
    setIsLoading(true);
    fetchSingleFormById(formId)
      .then(({ data }) => {
        const structure = data.structure
          ? (typeof data.structure === 'string' ? JSON.parse(data.structure) : data.structure)
          : [];
        setForm({ ...data, structure });
        if (!readOnly && aiSuggestions && Object.keys(aiSuggestions).length > 0) {
          setResponses(aiSuggestions);
        }
      })
      .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setIsLoading(false));
  // aiSuggestions intentionally excluded: only apply once on initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  useEffect(() => {
    if (readOnly && initialResponses) {
      setResponses(initialResponses);
    }
  }, [readOnly, initialResponses]);

  useEffect(() => {
    if (readOnly || userHasEdited || !form) return;
    if (!aiSuggestions || Object.keys(aiSuggestions).length === 0) return;
    setResponses(aiSuggestions);
  }, [aiSuggestions, form, userHasEdited, readOnly]);

  // Required-field validity also needs the form's required-file fields satisfied.
  const requiredFilesMissing = (() => {
    if (!form) return false;
    return flattenFields(form.structure, !!form.use_sections)
      .some((f) => f.required && f.type === 'file' && !(files[f.id]?.length));
  })();

  useEffect(() => {
    if (readOnly) return;
    onChange(
      { form_id: formId, responses, assigned_form_id: assignedFormId ?? null },
      isValid && !requiredFilesMissing,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, files, isValid, assignedFormId, formId, readOnly]);

  const handleValuesChange = (v: Record<string | number, any>) => {
    setUserHasEdited(true);
    setResponses(v);
  };

  if (!formId) return <Alert type="error" message="No form configured for this task." style={{ fontSize: 11 }} />;
  if (isLoading) return <Spin size="small" />;
  if (error || !form) return <Alert type="error" message="Failed to load form." style={{ fontSize: 11 }} />;

  const hasSuggestions = !readOnly && aiSuggestions !== undefined && Object.keys(aiSuggestions).length > 0;
  const suggestionsLoading = !readOnly && aiSuggestions === undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
      <Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--mg-text)' }}>{form.title}</Text>
      {hasSuggestions && (
        <Alert
          type="info"
          icon={<RobotOutlined />}
          showIcon
          message="AI pre-fill applied — review all values carefully"
          style={{ padding: '4px 8px', fontSize: 11 }}
        />
      )}
      {suggestionsLoading && (
        <Alert
          type="info"
          icon={<LoadingOutlined />}
          showIcon
          message="Analysing patient data to pre-fill this form…"
          style={{ padding: '4px 8px', fontSize: 11 }}
        />
      )}
      <UnifiedFormRenderer
        structure={form.structure}
        useSections={!!form.use_sections}
        values={responses}
        onValuesChange={handleValuesChange}
        files={files}
        onFilesChange={setFiles}
        onValidityChange={setIsValid}
        readOnly={readOnly}
        sectionMode="flat"
        fieldRefs={readOnly ? undefined : aiSuggestionRefs}
      />
    </div>
  );
};

export default CompletePractitionerFormTaskForm;
